import { z } from "zod";
import { Id, NumParam, Timestamp } from "./common.ts";

/**
 * 3D models for underwater objects (shown in dive mode, close to the diver).
 *
 * - ADMINS ONLY upload, replace, edit or delete a model (moderators → 403); one model per
 *   object. Divers can neither upload nor suggest models (not part of any suggestion payload).
 * - Format: glTF 2.0 binary (`.glb`, MODEL_CONTENT_TYPE), at most MODEL_MAX_BYTES. The API
 *   validates it before storing (see MODEL_LIMITS); a file that fails → 400 with the reason.
 *   The validator never decodes images or geometry: it reads the JSON chunk and PNG IHDR /
 *   JPEG SOF headers, and checks
 *   - every bufferView lies inside the BIN chunk, every accessor `byteOffset + count·stride`
 *     inside its bufferView, sparse indices < the accessor's count;
 *   - the decoded budget: Σ over accessors (incl. sparse, morph targets, animation samplers and
 *     EXT_meshopt_compression's declared `count × byteStride`) ≤ maxDecodedBytes;
 *   - texture pixels: each image ≤ maxTexturePx per side, Σ width·height ≤ maxTotalTexturePx;
 *   - morph targets per mesh ≤ maxMorphTargets, joints per skin ≤ maxJointsPerSkin.
 * - Stored in PostgreSQL (so the nightly backup includes it), addressed by the SHA-256 of the
 *   file: the same file uploaded twice is stored once (`ON CONFLICT (sha256) DO NOTHING`). A
 *   file is deleted only when no object references it, checked in the same transaction as the
 *   delete/replace. Total stored model bytes are capped at MODEL_TOTAL_MAX_BYTES (→ 409).
 * - Upload transport: auth (admin), then the Origin check, run BEFORE the body is read.
 *   `Content-Length` over MODEL_MAX_BYTES → 413 at once; otherwise the streamed body is counted
 *   and aborted at MODEL_MAX_BYTES + 1 (→ 413, `payload_too_large`), never buffered first.
 *   Uploads are rate-limited per admin (10/min).
 * - Serving files: per-IP rate limit (60/min, like terrain); served from a content-addressed
 *   disk cache (`<sha256>.glb`, written on upload, filled on first read) or streamed from the
 *   DB — never buffered whole in the app heap per request. Visibility is checked on every
 *   request through object_models → objects (status, deleted_at), never a cached flag.
 *   `Cache-Control: public, max-age=86400` + `ETag: "<sha256>"` (304) when a published object
 *   uses it, `private, no-cache` when only staff may see it; never `immutable` (an unpublish
 *   or licence takedown must take effect within a day). The service worker never caches
 *   /api/object-models (classify "never").
 * - Client: loads models lazily in a Dive* chunk with GLTFLoader + MeshoptDecoder (registered
 *   lazily); ignores nothing in the whitelist.
 * - Placement: model units are metres, Y up, the model's front is +Z. The model is scaled by
 *   `scale` and turned so that `headingDeg` is the compass direction its FRONT faces, clockwise
 *   from LV95 grid north seen from above (0 = front faces north, 90 = east). In the scene
 *   (x east, z south) the front direction is (sin h, 0, −cos h), i.e. `rotation.y = π − h`, the
 *   same convention as the dive-mode diver. It is
 *   placed so that the bottom centre of its scaled bounding box sits at the object's location,
 *   at the object's resolved depth (or on the bed when the depth is unknown and the bed is
 *   known; not shown at all when neither is known).
 * - Every model carries an `attribution` (source and licence, shown in dive mode).
 * - Visibility follows the object: anonymous callers see models of published, non-deleted
 *   objects only; staff (moderators included) also see drafts. A model of an invisible object → 404, file included.
 * - `DiveObject` itself is unchanged (cached older app versions keep parsing it); models are
 *   fetched separately.
 */
export const MODEL_CONTENT_TYPE = "model/gltf-binary";
export const MODEL_MAX_BYTES = 8 * 1024 * 1024;
/** Soft cap on all stored model files together (→ 409 conflict on upload). */
export const MODEL_TOTAL_MAX_BYTES = 2 * 1024 * 1024 * 1024;

/** What the API accepts. Anything else → 400 bad_request with a message naming the rule. */
export const MODEL_LIMITS = {
  /** Sum over all mesh primitives (triangles; non-indexed: POSITION count / 3). */
  maxTriangles: 150_000,
  maxTextures: 16,
  /** Width and height of every embedded PNG/JPEG image (read from IHDR/SOF, never decoded). */
  maxTexturePx: 2048,
  /** Σ width·height over all images (16 MP ≈ 4 × 2048², ≈ 64 MiB as RGBA). */
  maxTotalTexturePx: 16 * 1024 * 1024,
  /** Σ decoded accessor bytes (geometry, sparse, morph targets, animations, meshopt-declared). */
  maxDecodedBytes: 64 * 1024 * 1024,
  maxMorphTargets: 8,
  maxJointsPerSkin: 128,
  maxJsonBytes: 1024 * 1024,
  /** Only these may appear in `extensionsUsed`/`extensionsRequired`. No Draco, no Basis/KTX2 yet. */
  allowedExtensions: [
    "KHR_mesh_quantization",
    "KHR_texture_transform",
    "KHR_materials_emissive_strength",
    "KHR_materials_unlit",
    "KHR_materials_ior",
    "KHR_materials_specular",
    "KHR_materials_clearcoat",
    "EXT_meshopt_compression",
  ],
  /** Embedded images only (bufferView), no `uri` on buffers or images (no external or data URIs). */
  imageMimeTypes: ["image/png", "image/jpeg"],
} as const;

export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);

export const ObjectModel = z
  .object({
    objectId: Id,
    sha256: Sha256Hex,
    /** Relative URL of the file: `/api/object-models/<sha256>.glb`. */
    url: z.string().regex(/^\/api\/object-models\/[0-9a-f]{64}\.glb$/),
    sizeBytes: z.number().int().min(1).max(MODEL_MAX_BYTES),
    triangles: z.number().int().min(0).max(MODEL_LIMITS.maxTriangles),
    /** Size of the unscaled bounding box in metres (x, y, z). */
    boundsM: z
      .object({ x: z.number().min(0), y: z.number().min(0), z: z.number().min(0) })
      .strict(),
    /** Clip names, if the file has animations (dive mode loops the first one). */
    animations: z.array(z.string().max(120)).max(32),
    scale: z.number().min(0.01).max(100),
    headingDeg: z.number().min(0).lt(360),
    attribution: z.string().trim().min(1).max(300),
    uploadedAt: Timestamp,
    updatedAt: Timestamp,
  })
  .strict()
  .refine((m) => m.url === `/api/object-models/${m.sha256}.glb`, {
    message: "url must name the sha256",
    path: ["url"],
  });
export type ObjectModel = z.infer<typeof ObjectModel>;

/** GET /api/object-models?spotId=|lake= (exactly one) → the visible objects' models. */
export const ObjectModelListQuery = z
  .object({
    spotId: Id.optional(),
    lake: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
  })
  .strict()
  .refine((q) => (q.spotId === undefined) !== (q.lake === undefined), {
    message: "pass exactly one of spotId or lake",
  });
export type ObjectModelListQuery = z.infer<typeof ObjectModelListQuery>;

export const ObjectModelList = z.object({ models: z.array(ObjectModel) }).strict();
export type ObjectModelList = z.infer<typeof ObjectModelList>;

/**
 * PUT /api/objects/:id/model — the raw `.glb` as the request body (Content-Type
 * MODEL_CONTENT_TYPE, ≤ MODEL_MAX_BYTES, else 413), metadata in the query. Creates or replaces
 * the object's model; audited. A replaced file no other object uses is deleted.
 */
export const ObjectModelUploadQuery = z
  .object({
    attribution: z
      .string()
      .trim()
      .min(1)
      .max(300)
      .refine((s) => !s.includes("\u0000"), "attribution contains NUL"),
    scale: NumParam.pipe(z.number().min(0.01).max(100)).default(1),
    headingDeg: NumParam.pipe(z.number().min(0).lt(360)).default(0),
  })
  .strict();
export type ObjectModelUploadQuery = z.infer<typeof ObjectModelUploadQuery>;

/** PATCH /api/objects/:id/model — placement and attribution only; audited. */
export const ObjectModelUpdate = z
  .object({
    scale: z.number().min(0.01).max(100),
    headingDeg: z.number().min(0).lt(360),
    attribution: z.string().trim().min(1).max(300),
  })
  .partial()
  .strict();
export type ObjectModelUpdate = z.infer<typeof ObjectModelUpdate>;

/** `:file` of GET /api/object-models/:file — `<sha256>.glb`. */
export const ObjectModelFileParam = z
  .object({ file: z.string().regex(/^[0-9a-f]{64}\.glb$/) })
  .strict();
